import { useMutation } from '@tanstack/react-query'
import { X, ArrowUpFromLine, CheckCircle, Loader2, ExternalLink } from 'lucide-react'
import api from '../../services/api'

interface MendeleyExportProps {
  isOpen: boolean
  onClose: () => void
  resourceId: string
  title: string
  resourceType: string
}

export default function MendeleyExport({
  isOpen,
  onClose,
  resourceId,
  title,
  resourceType,
}: MendeleyExportProps) {
  const exportMutation = useMutation({
    mutationFn: async () => {
      const { data } = await api.post('/academic/mendeley/export', {
        resource_id: resourceId,
        resource_type: resourceType,
      })
      return data
    },
  })

  const handleExport = () => {
    exportMutation.mutate()
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4 p-6">
        {exportMutation.isSuccess ? (
          <div className="text-center py-6">
            <CheckCircle className="mx-auto h-10 w-10 text-green-500 mb-3" />
            <p className="text-base font-medium text-gray-900">Exported to Mendeley</p>
            <p className="text-sm text-gray-500 mt-1 mb-4">
              &ldquo;{title}&rdquo; has been added to your Mendeley library.
            </p>
            <div className="flex items-center justify-center gap-3">
              {exportMutation.data?.mendeley_url && (
                <a
                  href={exportMutation.data.mendeley_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  <ExternalLink className="h-4 w-4" />
                  View in Mendeley
                </a>
              )}
              <button
                onClick={onClose}
                className="rounded-lg bg-acad-500 px-4 py-2 text-sm font-medium text-white hover:bg-acad-600 transition-colors"
              >
                Done
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <ArrowUpFromLine className="h-5 w-5 text-acad-600" />
                <h2 className="text-lg font-semibold text-gray-900">Export to Mendeley</h2>
              </div>
              <button
                onClick={onClose}
                className="p-1 text-gray-400 hover:text-gray-600 rounded"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Resource details */}
            <div className="rounded-lg bg-gray-50 border border-gray-200 p-4 mb-4">
              <p className="text-sm font-medium text-gray-900">{title}</p>
              <span className="inline-flex items-center rounded-full bg-acad-50 px-2 py-0.5 text-xs font-medium text-acad-600 capitalize mt-2">
                {resourceType}
              </span>
            </div>

            <p className="text-sm text-gray-500 mb-4">
              This resource will be exported to your connected Mendeley library as a reference.
            </p>

            {/* Error */}
            {exportMutation.isError && (
              <div className="mb-4 rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-700">
                Export failed. Make sure your Mendeley account is connected and try again.
              </div>
            )}

            <div className="flex justify-end gap-3">
              <button
                onClick={onClose}
                className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleExport}
                disabled={exportMutation.isPending}
                className="inline-flex items-center gap-2 rounded-lg bg-acad-500 px-4 py-2 text-sm font-medium text-white hover:bg-acad-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {exportMutation.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Exporting...
                  </>
                ) : (
                  <>
                    <ArrowUpFromLine className="h-4 w-4" />
                    Export to Mendeley
                  </>
                )}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
